/**
 * queue/components/createQueueDialog.tsx — "Request a queue" (rev 5).
 * Faithful port of the design prototype's request-a-queue modal: a product asks
 * the engine for a line, defining intake + who consumes the output. Fields:
 * Requesting product · Feature/line name · Match type (FITTED/POOL) · Resource
 * noun · Unit noun (FITTED only) · Output consumer (+ Webhook / Other custom).
 *
 * Maps to the backend POST /v1/queues contract (verified against sk-queue-api
 * rev-5 validation/queue.ts + queueConfigService coherence rules):
 *   FITTED → product WAITLIST; POOL → product CHAT (per_unit_capacity default 3,
 *   the researched norm). requesting_product_ref / output_consumer_ref carry the
 *   platform catalog refs; a custom endpoint (Webhook / Other) goes to
 *   output_consumer_endpoint. vocab carries the display-only nouns.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity } from 'react-native';
import Modal from '../../components/modal';
import { useTheme } from '../../context/themeContext';
import { useActiveQueue } from '../activeQueue';
import { useSession } from '../../context/sessionContext';
import { createQueue, getCatalogProducts } from '../../services/queue/queueService';
import { handleApiError } from '../../utils/errorHandler';
import { showToast } from '../../components/toastComponent';
import { Mono } from './atoms';
import type { CatalogProduct, CreateQueueRequest } from '../../api/queueDtos';

/** name → url-safe slug (matches the backend's lowercase/hyphen rule). */
export const slugify = (s: string) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);

// Non-catalog output-consumer choices (prototype: Webhook / API + Other…).
const WEBHOOK = 'Webhook / API';
const OTHER = 'Other…';

// Allocation mode chosen at create. POOL splits into VOICE (one global
// max_parallel) and CHAT (per-agent capacity + least-loaded) — two different
// engine rules (SPEC §6.2), so the user picks explicitly; requesting-product /
// output-consumer are attribution/routing and don't determine this.
type Mode = 'FITTED' | 'VOICE' | 'CHAT';

// Per-mode → backend product + match_type + sensible default display vocab, so a
// Voice queue reads "call slot/caller" and a Chat queue reads "agent/chat"
// instead of the generic POOL "slots" fallback.
const MODE_DEFAULTS: Record<Mode, { product: CreateQueueRequest['product']; matchType: 'FITTED' | 'POOL'; resource: string; unit: string }> = {
  FITTED: { product: 'WAITLIST', matchType: 'FITTED', resource: '', unit: 'party' },
  VOICE: { product: 'VOICE', matchType: 'POOL', resource: 'call slot', unit: 'caller' },
  CHAT: { product: 'CHAT', matchType: 'POOL', resource: 'agent', unit: 'chat' },
};

export default function CreateQueueDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { theme } = useTheme();
  const { colors } = theme;
  const { reload, setActiveQueue } = useActiveQueue();
  const { entitlements } = useSession();

  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [productRef, setProductRef] = useState('');
  const [feature, setFeature] = useState('');
  const [mode, setMode] = useState<Mode>('FITTED');
  const [resource, setResource] = useState('');
  const [unit, setUnit] = useState('party');
  const [consumerRef, setConsumerRef] = useState(''); // '' = default (= requester); or a ref / WEBHOOK / OTHER
  const [consumerCustom, setConsumerCustom] = useState('');
  const [busy, setBusy] = useState(false);

  // Fetch the platform catalog when the modal opens.
  useEffect(() => {
    if (!open) return;
    getCatalogProducts()
      .then((list) => {
        setProducts(list);
        if (list.length && !productRef) setProductRef(list[0].ref);
      })
      .catch((err) => handleApiError(err, 'Could not load product catalog'));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = () => {
    setFeature(''); setMode('FITTED'); setResource(''); setUnit('party');
    setConsumerRef(''); setConsumerCustom(''); setBusy(false);
  };
  const close = () => { if (!busy) { reset(); onClose(); } };

  const isFitted = mode === 'FITTED';

  // Output-consumer options: requester first, then the rest, then Webhook / Other.
  const consumerOptions = useMemo(() => {
    const reqFirst = products.slice().sort((a, b) => (a.ref === productRef ? -1 : b.ref === productRef ? 1 : 0));
    return [
      ...reqFirst.map((p) => ({ v: p.ref, label: p.label + (p.ref === productRef ? ' (requester)' : '') })),
      { v: WEBHOOK, label: WEBHOOK },
      { v: OTHER, label: OTHER },
    ];
  }, [products, productRef]);
  const consumerIsCustom = consumerRef === WEBHOOK || consumerRef === OTHER;

  const submit = async () => {
    const feat = feature.trim();
    if (!feat) { showToast('error', 'Name required', 'Name the queue (feature) first.'); return; }
    const slug = slugify(feat);
    if (!slug) { showToast('error', 'Invalid name', 'Use letters or numbers in the feature name.'); return; }
    if (consumerIsCustom && !consumerCustom.trim()) {
      showToast('error', 'Endpoint required', 'Enter the custom endpoint / integration name.');
      return;
    }
    setBusy(true);
    try {
      // Mode → backend product + match_type (FITTED=WAITLIST; VOICE/CHAT=POOL).
      // VOICE = one global max_parallel; CHAT = per-agent capacity (3 = norm).
      const def = MODE_DEFAULTS[mode];
      const product = def.product;
      const productLabel = products.find((p) => p.ref === productRef)?.label ?? 'Queue';
      // Display nouns: user override, else the per-mode default (call slot/agent).
      const resourceNoun = resource.trim() || def.resource;
      const unitNoun = isFitted ? (unit.trim() || def.unit) : def.unit;

      const body: CreateQueueRequest = {
        // Human name = "<product> · <feature>", like the prototype's q.name.
        name: `${productLabel} · ${feat}`.slice(0, 160),
        slug,
        product,
        matchType: def.matchType,
        feature: feat,
        requestingProductRef: productRef || undefined,
        // Custom endpoint (Webhook / Other) → outputConsumerEndpoint; a real
        // product ref → outputConsumerRef; blank → backend defaults to requester.
        ...(consumerIsCustom
          ? { outputConsumerEndpoint: consumerCustom.trim() }
          : consumerRef
            ? { outputConsumerRef: consumerRef }
            : {}),
        vocab: {
          resource: resourceNoun || undefined,
          resourcePl: resourceNoun ? `${resourceNoun}s` : undefined,
          unit: unitNoun || undefined,
          hasParty: isFitted && !!unitNoun,
        },
        // POOL concurrency defaults (backend rejects VOICE w/o max_parallel, CHAT
        // w/o per_unit_capacity): Voice=8 concurrent call slots, Chat=3 per agent.
        ...(product === 'VOICE' ? { maxParallel: 8 } : {}),
        ...(product === 'CHAT' ? { perUnitCapacity: 3 } : {}),
      };

      const { queue } = await createQueue(body);
      showToast('success', 'Queue provisioned', `${productLabel} · ${feat}`);
      reload();
      setActiveQueue(queue.id);
      reset();
      onClose();
    } catch (err) {
      handleApiError(err, 'Could not provision queue');
    } finally {
      setBusy(false);
    }
  };

  const label = { fontSize: 12.5, fontWeight: '600' as const, color: colors.ink, marginBottom: 7 };
  const input = {
    width: '100%', height: 44, borderWidth: 1, borderColor: colors.lineStrong, borderRadius: 10,
    paddingHorizontal: 14, fontSize: 14, color: colors.ink,
  } as const;

  const footer = (
    <>
      <TouchableOpacity onPress={close} disabled={busy}
        style={{ height: 42, paddingHorizontal: 18, borderRadius: 10, borderWidth: 1, borderColor: colors.lineStrong, alignItems: 'center', justifyContent: 'center', opacity: busy ? 0.5 : 1 }}>
        <Text style={{ fontSize: 14, fontWeight: '500', color: colors.ink }}>Cancel</Text>
      </TouchableOpacity>
      <TouchableOpacity onPress={submit} disabled={busy}
        style={{ height: 42, paddingHorizontal: 18, borderRadius: 10, backgroundColor: colors.ink, alignItems: 'center', justifyContent: 'center', opacity: busy ? 0.6 : 1 }}>
        <Text style={{ fontSize: 14, fontWeight: '600', color: colors.white }}>{busy ? 'Provisioning…' : 'Provision queue ▸'}</Text>
      </TouchableOpacity>
    </>
  );

  return (
    <Modal open={open} onClose={close} title="Request a queue" footer={footer}>
      <View style={{ gap: 18 }}>
        <Text style={{ fontSize: 12.5, color: colors.ink4, marginTop: -4 }}>
          A product asks the engine for a line. Define intake + who consumes the output.
        </Text>
        {/* Requesting product */}
        <View>
          <Text style={label}>Requesting product</Text>
          <Dropdown value={productRef} onChange={setProductRef}
            options={products.map((p) => ({ v: p.ref, label: p.label }))} colors={colors} />
        </View>

        {/* Feature / line name */}
        <View>
          <Text style={label}>Feature / line name</Text>
          <TextInput value={feature} onChangeText={setFeature} autoFocus
            placeholder="e.g. Teller Counter, Pharmacy Pickup" placeholderTextColor={colors.ink4}
            onSubmitEditing={submit} style={input as any} />
          {!!feature.trim() && (
            <Text style={{ fontSize: 11, color: colors.ink4, marginTop: 4 }}>Link slug: {slugify(feature) || '—'}</Text>
          )}
        </View>

        {/* Allocation mode. FITTED (sized/exclusive) vs the two POOL products —
            Voice (one global limit) and Chat (per-agent). Voice/Chat are shown
            only when the tenant is entitled to that product. */}
        <View>
          <Text style={label}>Match type</Text>
          <View style={{ flexDirection: 'row', gap: 10 }}>
            <MatchCard title="FITTED" desc="sized resource · exclusive" active={mode === 'FITTED'} onPress={() => setMode('FITTED')} colors={colors} />
            {entitlements.voice && (
              <MatchCard title="VOICE" desc="POOL · call slots" active={mode === 'VOICE'} onPress={() => setMode('VOICE')} colors={colors} />
            )}
            {entitlements.chat && (
              <MatchCard title="CHAT" desc="POOL · agents" active={mode === 'CHAT'} onPress={() => setMode('CHAT')} colors={colors} />
            )}
          </View>
        </View>

        {/* Resource + Unit nouns */}
        <View style={{ flexDirection: 'row', gap: 12 }}>
          <View style={{ flex: 1 }}>
            <Text style={label}>Resource noun</Text>
            <TextInput value={resource} onChangeText={setResource}
              placeholder="table / room / counter / agent" placeholderTextColor={colors.ink4} style={input as any} />
          </View>
          {isFitted && (
            <View style={{ flex: 1 }}>
              <Text style={label}>Unit noun <Text style={{ color: colors.ink4, fontWeight: '400' }}>(opt)</Text></Text>
              <TextInput value={unit} onChangeText={setUnit}
                placeholder="party / patient / —" placeholderTextColor={colors.ink4} style={input as any} />
            </View>
          )}
        </View>

        {/* Output consumer */}
        <View>
          <Text style={label}>Output consumer <Text style={{ color: colors.ink4, fontWeight: '400' }}>— registered product from Platform</Text></Text>
          <Dropdown value={consumerRef || (products[0]?.ref ?? '')} onChange={setConsumerRef}
            options={consumerOptions} colors={colors} />
          {consumerIsCustom && (
            <TextInput value={consumerCustom} onChangeText={setConsumerCustom}
              placeholder="Custom endpoint / integration name" placeholderTextColor={colors.ink4}
              style={[input, { marginTop: 8 }] as any} />
          )}
        </View>
      </View>
    </Modal>
  );
}

/** Segmented allocation-mode card (FITTED / VOICE / CHAT). */
function MatchCard({ title, desc, active, onPress, colors }: {
  title: string; desc: string; active: boolean; onPress: () => void; colors: any;
}) {
  return (
    <TouchableOpacity onPress={onPress}
      style={{
        flex: 1, padding: 12, borderRadius: 10, borderWidth: 1.5,
        backgroundColor: active ? colors.ink : colors.card,
        borderColor: active ? colors.ink : colors.line,
      }}>
      <Mono style={{ fontWeight: '600', fontSize: 14, color: active ? colors.white : colors.ink }}>{title}</Mono>
      <Text style={{ fontSize: 11, marginTop: 2, color: active ? colors.white : colors.ink4, opacity: active ? 0.7 : 1 }}>{desc}</Text>
    </TouchableOpacity>
  );
}

/**
 * A monochrome select. On web we render a native <select> (proper dropdown);
 * elsewhere a tap-cycle button. Kept inline to avoid a new shared dependency.
 */
function Dropdown({ value, onChange, options, colors }: {
  value: string; onChange: (v: string) => void; options: Array<{ v: string; label: string }>; colors: any;
}) {
  const { Platform } = require('react-native');
  if (Platform.OS === 'web') {
    const Select: any = 'select';
    const Option: any = 'option';
    return (
      <Select value={value} onChange={(e: any) => onChange(e.target.value)}
        style={{ width: '100%', height: 44, border: `1px solid ${colors.lineStrong}`, borderRadius: 10, padding: '0 12px', fontSize: 14, background: colors.card, color: colors.ink, outline: 'none', cursor: 'pointer', fontFamily: 'IBM Plex Sans' }}>
        {options.map((o) => (
          <Option key={o.v} value={o.v}>{o.label}</Option>
        ))}
      </Select>
    );
  }
  // Native fallback: cycle through options on tap.
  const idx = Math.max(0, options.findIndex((o) => o.v === value));
  const cur = options[idx] ?? options[0];
  return (
    <TouchableOpacity
      onPress={() => onChange(options[(idx + 1) % options.length]?.v ?? value)}
      style={{ height: 44, borderWidth: 1, borderColor: colors.lineStrong, borderRadius: 10, paddingHorizontal: 12, justifyContent: 'center' }}>
      <Text style={{ fontSize: 14, color: colors.ink }}>{cur?.label ?? '—'}</Text>
    </TouchableOpacity>
  );
}
